import { Button } from '@/components/ui/button'
import { Command, CommandItem, CommandList } from '@/components/ui/command'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useQueryHelper } from '@/@diffrag/hooks/useQueryHelper'

export type TableFilter = {
  key: string
  label: string
  multiple?: boolean
  items: {
    value: string
    label: string
  }[]
}

type Props = {
  filters: TableFilter[]
  allSelect?: boolean
}

export const SimpleTableFilter = ({ filters, allSelect }: Props) => {
  const { query, replaceQuery } = useQueryHelper()

  const onSelect = (
    filter: TableFilter,
    item: { value: string; label: string },
  ) => {
    if (filter.multiple) {
      const currentValues = safeArrayParse(query[filter.key] as string)

      replaceQuery({
        [filter.key]: currentValues.includes(item.value)
          ? currentValues.filter((v: string) => v !== item.value)
          : [...currentValues, item.value],
      })
    } else {
      replaceQuery({
        [filter.key]: item.value,
      })
    }
  }

  const onReset = (filter: TableFilter) => {
    replaceQuery({
      [filter.key]: '',
    })
  }

  const safeArrayParse = (value: string) => {
    try {
      return JSON.parse(value)
    } catch (error) {
      return []
    }
  }

  const isSelected = (
    filter: TableFilter,
    item: { value: string; label: string },
  ) => {
    if (filter.multiple) {
      return safeArrayParse(query[filter.key] as string).includes(item.value)
    } else {
      return query[filter.key] === item.value
    }
  }

  const isEmpty = (filter: TableFilter) => {
    if (filter.multiple) {
      return !safeArrayParse(query[filter.key] as string).length
    } else {
      return !query[filter.key]
    }
  }

  const isAllSelected = (filter: TableFilter) => {
    if (isEmpty(filter)) {
      return true
    }

    if (filter.multiple) {
      return (
        safeArrayParse(query[filter.key] as string).length ===
        filter.items.length
      )
    }

    return !query[filter.key]
  }

  return (
    <div className="flex items-center gap-2">
      <p className="text-sm text-gray-500">검색 필터</p>
      {filters.map((filter) => {
        return (
          <Popover>
            <PopoverTrigger>
              <Button variant="outline" size="sm">
                {filter.label}
                <ChevronDownIcon className="size-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0">
              <Command>
                <CommandList>
                  {allSelect && (
                    <CommandItem
                      className="flex items-center justify-between gap-2 px-4 py-2"
                      onSelect={() => {
                        onReset(filter)
                      }}
                    >
                      <span>전체</span>
                      {isAllSelected(filter) && (
                        <CheckIcon className="size-4" />
                      )}
                    </CommandItem>
                  )}

                  {filter.items.map((item) => {
                    return (
                      <CommandItem
                        key={item.value}
                        value={item.value}
                        onSelect={() => {
                          onSelect(filter, item)
                        }}
                        className="flex items-center justify-between gap-2 px-4 py-2"
                      >
                        <span>{item.label}</span>
                        {isSelected(filter, item) && (
                          <CheckIcon className="size-4" />
                        )}
                      </CommandItem>
                    )
                  })}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )
      })}
    </div>
  )
}
